import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Modal({ open, onClose, children, className = "", labelledBy, closeDisabled = false }) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const openerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [viewport, setViewport] = useState({ height: "100dvh", top: 0 });

  useEffect(() => {
    if (!open) return;
    const visualViewport = window.visualViewport;
    const updateViewport = () => setViewport({
      height: `${visualViewport?.height || window.innerHeight}px`,
      top: visualViewport?.offsetTop || 0,
    });
    updateViewport();
    visualViewport?.addEventListener("resize", updateViewport);
    visualViewport?.addEventListener("scroll", updateViewport);
    window.addEventListener("resize", updateViewport);
    return () => {
      visualViewport?.removeEventListener("resize", updateViewport);
      visualViewport?.removeEventListener("scroll", updateViewport);
      window.removeEventListener("resize", updateViewport);
    };
  }, [open]);

  // Dialog parents commonly create their close callback inline. Keep the latest
  // callback without treating that render as a new dialog open: re-running the
  // focus effect would steal focus from the field being typed in.
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusables = () => Array.from(panelRef.current?.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) || []).filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true" && !element.closest('[aria-hidden="true"]'));
    const focusFirst = () => (closeRef.current || focusables()[0] || panelRef.current)?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") { event.preventDefault(); if (!closeDisabled) onCloseRef.current?.(); return; }
      if (event.key !== "Tab") return;
      const available = focusables();
      if (!available.length) { event.preventDefault(); panelRef.current?.focus(); return; }
      const first = available[0]; const last = available[available.length - 1];
      if (event.shiftKey && (document.activeElement === first || !panelRef.current?.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !panelRef.current?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    focusFirst();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      const opener = openerRef.current;
      if (opener?.isConnected) opener.focus();
    };
  }, [open, closeDisabled]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-x-0 z-50 grid place-items-end sm:place-items-center bg-black/70 backdrop-blur-sm animate-fade-in-up"
      style={{ top: viewport.top, height: viewport.height }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onClick={() => { if (!closeDisabled) onClose?.(); }}
      data-testid="modal-backdrop"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "w-full max-w-[100vw] sm:max-w-lg card-surface rounded-t-2xl sm:rounded-2xl overflow-x-hidden overflow-y-auto",
          className
        )}
        style={{ maxHeight: `calc(${viewport.height} - env(safe-area-inset-top) - env(safe-area-inset-bottom))` }}
      >
        <div className="sticky top-0 z-20 flex items-center justify-end px-2 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] bg-bg-surface/95 backdrop-blur border-b border-line">
          <button
            ref={closeRef}
            onClick={() => { if (!closeDisabled) onClose?.(); }}
            disabled={closeDisabled}
            data-testid="modal-close-btn"
            className="h-11 w-11 grid place-items-center rounded-lg border border-line bg-bg-elevated text-ink-primary shadow-sm hover:border-brand/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 sm:p-6">{children}</div>
      </div>
    </div>
  );
}
